'use client';
import React from 'react';
import { signOut } from 'next-auth/react';
import { Button } from '@nextui-org/react';
import { useMemo, useState } from 'react';
import { useParams } from 'next/navigation';

interface SignOutButtonProps {
  children?: React.ReactNode;
  className?: string;
  showText?: boolean;
  redirectTo?: string;
  confirmSignOut?: boolean;
  isIconOnly? : boolean;
  ariaLabel?: string;
  variant?: 'flat' | 'bordered' | 'light' | 'shadow' | 'ghost' | 'solid';
}

export default function SignOutButton({
  children,
  className = '',
  showText = false,
  redirectTo,
  confirmSignOut = false,
  isIconOnly,
  ariaLabel,
  variant
}: SignOutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const params = useParams();

  const lang = useMemo(() => {
    if (!params) {
      return undefined;
    }

    const maybeLang = (params as Record<string, string | string[]>)?.lang;

    if (typeof maybeLang === 'string' && maybeLang.length > 0) {
      return maybeLang;
    }

    if (Array.isArray(maybeLang) && maybeLang.length > 0) {
      return maybeLang[0];
    }

    return undefined;
  }, [params]);

  const resolvedRedirectTo = useMemo(() => {
    if (redirectTo) {
      return redirectTo;
    }
    return lang ? `/${lang}` : '/';
  }, [lang, redirectTo]);

  const handleSignOut = async () => {
    if (confirmSignOut) {
      // Redirect to custom signout page
      const signOutPath = lang ? `/${lang}/auth/signout` : '/auth/signout';
      window.location.href = signOutPath;
      return;
    }

    setIsLoading(true);
    try {
      await signOut({
        callbackUrl: resolvedRedirectTo,
        redirect: true
      });
    } catch (error) {
      console.error('Sign out error:', error);
      setIsLoading(false);
    }
  };

  return (
    <Button
      isIconOnly={isIconOnly}
      aria-label={ariaLabel || 'Sign out'}
      onClick={handleSignOut}
      isLoading={isLoading}
      variant={variant} 
      className={className}
      disabled={isLoading}
    >
      {children || (showText ? 'Sign Out' : '')}
    </Button>
  );
}